import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, forwardRef, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Client } from '@df/core/service/client';
import { ImageModule } from '@df/module-image/image.module';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { KlevuTrackClickDirective } from '@df/module-klevu-search/klevu-track-click.directive';
import { SearchOutletComponentDef } from '@df/search/search-outlet.component.def';
import { Search } from '@df/search/search.interfaces';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { NToggle, ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { NListing } from '../../../catalog/ui/listing/product-listing.interface';
import { ProductComponent } from '../../../catalog/ui/product/product.component';
import { GtmImpressionTrackDirective } from '../../../module/gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../../module/gtm/gtm-track-product-click.directive';
import { SearchBannerComponent } from '../banner/search-banner.component';
import { SearchOutletBlockComponent } from './search-outlet-block.component';
export interface IPopularSearchesItem {
    name: string;
    link: string;
    image?: string;
}
@Component({
    selector: 'search-outlet',
    templateUrl: './search-outlet.component.html',
    styleUrls: ['./search-outlet.component.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        ImageModule,
        KlevuTrackClickDirective,
        NgClass,
        NgComponentOutlet,
        forwardRef(() => ProductComponent),
        RouterLink,
        SearchBannerComponent,
        SizeClassDirective,
        ToggleModule,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchOutletComponent extends SearchOutletComponentDef implements OnInit {
    readonly client: Client = inject(Client);
    readonly toggleService = inject(ToggleService);

    override ngOnInit(): void {
        super.ngOnInit();
        (<KlevuSearchService>this.searchService).loadKlevuVars(() => this._ref.detectChanges());
    }
    get shouldShow(): boolean {
        return !!this.result?.hasAny();
    }
    get products() {
        return this.result?.products?.filter(p => !p.data?.archive)?.slice(0, 4);
    }
    openTab(id: NToggle.TId): void {
        this.toggleService.getItem(id).open();
    }
    boldTerm(text: string): string {
        return text.replace(this.result?.term || '', '<b>' + this.result?.term + '</b>');
    }
    trackByIndex(index: number): number {
        return index;
    }

    get popularSearchesLabel(): string {
        return 'Suggested Results';
    }

    get popularSearches(): Search.ITerm[] | undefined {
        return this.terms?.slice(0, 5);
    }

    get suggestedPagesLabel(): string {
        return 'Blog Posts';
    }

    get suggestedPages(): IPopularSearchesItem[] | undefined {
        return this.result?.pages?.map(p => ({ name: this.boldTerm(p.data?.name || ''), link: p.data?.url || '', image: p.data?.image }));
    }

    get suggestedCategoriesLabel(): string {
        return '';
    }

    get suggestedCategories(): IPopularSearchesItem[] | undefined {
        return undefined;
    }

    get hasNonProductResults() {
        return this.result && (this.result?.terms?.length || this.result?.pages?.length);
    }

    get useKlevu() {
        return true;
    }

    get terms() {
        return this.result?.terms;
    }

    get term() {
        return this.result?.term;
    }

    get noResultsOutlet(): NListing.IOutlet {
        return { component: SearchOutletBlockComponent, inputs: { popularSearches: this.popularSearches } };
    }

    get clickCode(): string | undefined {
        return undefined;
    }
}
